import { ArrowDropDown } from '@mui/icons-material'
import { TableSortLabel } from '@mui/material'
import { OUColumn, SummaryByOURow } from '../../api'
import EmptyBox from '../../assets/empty_box.svg'
import FetchError from '../../assets/fetch_error.svg'
import {
  formatMTOTitle,
  getStatusClass,
  GLA_STRATEGIC_PRIORITY_LABEL,
  LEGEND_ENTRIES,
  SortColumn,
  SortDirection,
} from './summaryByOUConstants'
import styles from './AdoptionDashboard.module.css'

interface SummaryByOUMatrixTableProps {
  rows: SummaryByOURow[]
  ouColumns: OUColumn[]
  fetchError: boolean
  hasData: boolean
  selectedYear: number
  sortColumn: SortColumn
  sortDirection: SortDirection
  onSort: (column: SortColumn) => void
}

const SortableHeader = ({
  column,
  label,
  sortColumn,
  sortDirection,
  onSort,
}: {
  column: SortColumn
  label: string
  sortColumn: SortColumn
  sortDirection: SortDirection
  onSort: (column: SortColumn) => void
}) => (
  <TableSortLabel
    active={sortColumn === column}
    direction={sortColumn === column ? sortDirection : 'asc'}
    onClick={() => onSort(column)}
    IconComponent={ArrowDropDown}
    sx={{
      display: 'flex',
      width: '100%',
      justifyContent: 'space-between',
      '& .MuiTableSortLabel-icon': {
        color: 'rgba(30, 30, 30, 1) !important',
        fontSize: '30px',
        margin: 0,
      },
    }}>
    {label}
  </TableSortLabel>
)

const SummaryByOUMatrixTable = ({
  rows,
  ouColumns,
  fetchError,
  hasData,
  selectedYear,
  sortColumn,
  sortDirection,
  onSort,
}: SummaryByOUMatrixTableProps) => (
  <div className={styles.summaryByOUContainer}>
    {fetchError ? (
      <div className={styles.noExperimentsContainer}>
        <img src={FetchError} />
        <p className={styles.noExperimentsHeader}>Error Fetching Records</p>
        <p className={styles.noExperimentsText}>
          There was an issue retrieving the records. Check your connection and try again. If the error persists, contact
          the support team.
        </p>
      </div>
    ) : !hasData ? (
      <div className={styles.noExperimentsContainer}>
        <img src={EmptyBox} />
        <p className={styles.noExperimentsHeader}>No records found</p>
        <p className={styles.noExperimentsText}>Try adjusting your search or filter criteria</p>
      </div>
    ) : (
      <div className={styles.matrixScrollWrapper}>
        <table className={styles.matrixTable}>
          <colgroup>
            <col className={styles.colGLA} />
            <col className={styles.colBPD} />
            <col className={styles.colLaunched} />
            {ouColumns.map(col => (
              <col
                key={col.key}
                className={styles.colOU}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className={styles.sortableHeader}>
                <SortableHeader
                  column="title"
                  label={GLA_STRATEGIC_PRIORITY_LABEL}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
              </th>
              <th className={styles.sortableHeader}>
                <SortableHeader
                  column="decoded_learning"
                  label="BPD Name"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
              </th>
              <th className={styles.sortableHeader}>
                <SortableHeader
                  column="launched_in"
                  label="Launched in"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
              </th>
              {ouColumns.map(col => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const launchedIn =
                row.launched_quarter && row.launched_year ? `${row.launched_quarter}  ${row.launched_year}` : ''
              return (
                <tr key={`${row.title}-${row.decoded_learning}`}>
                  <td title={row.title}>{formatMTOTitle(row.title)}</td>
                  <td title={row.decoded_learning}>{row.decoded_learning}</td>
                  <td>{launchedIn}</td>
                  {ouColumns.map(col => (
                    <td key={col.key}>
                      <div className={`${styles.statusCell} ${getStatusClass(row.ou_statuses[col.key])}`}>
                        {row.ou_statuses[col.key] ?? ''}
                      </div>
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )}
    {hasData && (
      <div className={styles.legendContainer}>
        <p className={styles.legendText}>
          {LEGEND_ENTRIES.map((entry, idx) => (
            <span key={entry.status}>
              {idx > 0 && ' '}
              {entry.cssClass ? (
                <span className={entry.cssClass}>{entry.status}</span>
              ) : (
                <strong>{entry.status}</strong>
              )}
              {' = '}
              {entry.definition}
              {';'}
            </span>
          ))}
        </p>
      </div>
    )}
  </div>
)

export default SummaryByOUMatrixTable
